import React, { useEffect } from 'react';
import { MdCheck, MdClose } from 'react-icons/md';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import {
  errorsSelector,
  resetPasswordSelector,
  sendResetPasswordCode,
  validateResetPasswordCode,
} from '@src/features/auth';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import Button from '@src/components/ui2/Button';
import { CodeInput } from '@src/components/ui2/CodeInput';
import { Actions, OtpMessage, ResendCode, SubTitle } from './styles';
import { convertGeneralContextErrorToHookForm, getFormattedTime } from '@src/common';
import useCountdownTimer from '@src/hooks/UseCountDown';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import useAppEnvironment from '@src/hooks/useAppEnvironment';

type ResetPasswordFormProps = {
  email: string;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const ResetPasswordForm = (props: ResetPasswordFormProps) => {
  const { email, onSuccess = () => {}, onCancel = () => {} } = props;

  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const appEnv = useAppEnvironment();

  const contextErrors = useAppSelector(errorsSelector);
  const { loading, isCodeValid, requestLockedUntil } = useAppSelector(resetPasswordSelector);

  const { minutes, seconds, isOver } = useCountdownTimer(
    requestLockedUntil ? new Date(requestLockedUntil) : new Date(),
  );

  const { handleSubmit, control, formState, setError } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n)),
  });
  const { errors } = formState;

  const sendCode = () => {
    dispatch(
      sendResetPasswordCode({
        email: email,
      }),
    );
  };

  const submit = (payload: FieldValues) => {
    dispatch(
      validateResetPasswordCode({
        email: email ?? '',
        code: payload['code'],
      }),
    );
  };

  useEffect(() => {
    if (appEnv === 'production') {
      sendCode();
    }
  }, []);

  useEffect(() => {
    convertGeneralContextErrorToHookForm('code', setError, contextErrors, 'valid_code');
  }, [contextErrors]);

  useEffect(() => {
    if (isCodeValid) {
      onSuccess();
    }
  }, [isCodeValid]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <SubTitle>{i18n('direct_link_reset_password_form_title')}</SubTitle>

      <Controller
        name='code'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <CodeInput
            value={value}
            onChange={(e) => onChange(e)}
            label={i18n('direct_link_reset_password_code_label')}
            name={name}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            {...rest}
          />
        )}
      />

      {loading ? (
        <OtpMessage>{i18n('direct_link_reset_password_sending_code')}</OtpMessage>
      ) : !isOver ? (
        <OtpMessage>
          {i18n('direct_link_reset_password_code_expires')} {getFormattedTime(minutes)}:{getFormattedTime(seconds)}
        </OtpMessage>
      ) : (
        <ResendCode onClick={() => sendCode()}>{i18n('direct_link_reset_password_resend_code')}</ResendCode>
      )}

      <Actions>
        <Button outlined onClick={onCancel}>
          <MdClose />
          {i18n('direct_link_reset_password_cancel')}
        </Button>

        <Button type='submit'>
          <MdCheck />
          {i18n('direct_link_reset_password_confirm')}
        </Button>
      </Actions>
    </form>
  );
};

export default ResetPasswordForm;
